/**
 * IonActionEcho — S2 "Action-Echo": attributes every Ion-DRIVEN UI change to
 * Ion, so it reads as "Ion did that for me" instead of a stray bug. A
 * presentation-only consumer of `commandProvenance` (state/commandLayer.js) —
 * it writes nothing back, it only watches.
 *
 * Two cues per Ion action, on the affected control group(s):
 *   (a) a short-lived orange pulse (reuses the tour's `tourPulseGlow`
 *       keyframe — see ion.css's `.ion-action-echo-pulse` — same visual
 *       language as `highlightTarget`, just finite instead of infinite)
 *   (b) a transient "✦ by Ion" chip near the (first) affected control
 *
 * Always-mounted (like IonSurface) — see AppShell.jsx. Gated the same way,
 * via isIonVisible(), computed in render so @preact/signals' auto-tracking
 * re-evaluates it whenever chatAccess/ionHidden change (mirrors IonSurface's
 * own `visible` computation).
 *
 * source==='user' NEVER echoes — users don't need to be told they clicked
 * their own filter. `command:'revert'|'commit'` also never echoes (no
 * selector maps to them) — those are transaction bookkeeping, not a
 * user-visible control change.
 */

import { useEffect, useRef, useState } from 'preact/hooks'
import { useSignalEffect } from '@preact/signals'
import { createPortal } from 'preact/compat'
import { useLocation } from 'wouter-preact'
import { commandProvenance } from '../../state/commandLayer.js'
import { chatAccess, ionHidden } from '../../state/chatStore.js'
import { isIonVisible } from './ionGating.js'

// A real Ion turn that sets filters + re-sorts near-instantly (e.g. the
// ion-intro demo's `intro-drive` step, or a single tool-call turn) fires
// several commandProvenance writes back to back. Coalescing them into one
// echo is what makes that read as ONE attributed action instead of a
// pulse-stutter. The onboarding demo spaces its 3 calls ~700ms apart, well
// outside this window, so it legitimately produces separate echoes.
const COALESCE_WINDOW_MS = 150
// Matches `.ion-action-echo-pulse`'s `tourPulseGlow 0.8s … 2` (ion.css) —
// class is removed right after the animation finishes so it never lingers.
const PULSE_DURATION_MS = 1600
const CHIP_DURATION_MS = 2500

/**
 * Maps a commandProvenance `command` to the `[data-tour]` selector(s) of the
 * control group(s) it visibly affects. `revert`/`commit` and any unmapped
 * command return an empty array — no echo.
 * @param {string} command
 * @returns {string[]}
 */
export function resolveEchoGroups(command) {
  switch (command) {
    case 'filter':
      return ['[data-tour="explorer-filter-panel"]']
    case 'view':
      return ['[data-tour="explorer-view-switcher"]', '[data-tour="explorer-sort-controls"]']
    case 'context':
      return ['[data-tour="welcome-global-controls"]']
    case 'plot':
      // The two controls a person would have used by hand: the colour/size
      // selects in the context bar, and the axis card in the settings drawer.
      // Both anchors already exist for the ion-intro tour.
      return ['[data-tour="graph-color-size"]', '[data-tour="graph-expert-axes"]']
    case 'columns':
      // No dedicated anchor for the column picker, so pulse the view switcher
      // the table lives behind. An unmapped command returns [] and the echo
      // silently does nothing — a missing pulse is invisible, which is why
      // `plot` and `columns` were added the moment their commands shipped.
      return ['[data-tour="explorer-view-switcher"]']
    default:
      return []
  }
}

/**
 * Pure reducer for the coalescing window: given the accumulator so far
 * (`null` when no window is open) and a new commandProvenance write, returns
 * the next accumulator — or `null` if the write should be ignored entirely
 * (wrong source, unmapped command, or not newer than the last-handled `seq`).
 *
 * M2 fix: dedup on `seq` (a strictly-increasing counter, commandLayer.js),
 * not `at` (wall-clock ms) — two Ion commands landing in the SAME
 * millisecond (e.g. a synchronous set_filters + set_view in one real Ion
 * turn) have equal `at` but distinct `seq`, so the second one no longer gets
 * dropped as "not newer".
 *
 * Kept pure/DOM-free so the coalescing logic itself is unit-testable without
 * fake timers or a mounted component.
 *
 * @param {{selectors: Set<string>, firstAt: number, lastAt: number, lastSeq: number}|null} acc
 * @param {{command: string, source: string, at: number, seq: number}|null} provenance
 * @param {number|null} lastHandledSeq
 */
export function accumulateEcho(acc, provenance, lastHandledSeq) {
  if (!provenance || provenance.source !== 'ion') return null
  if (lastHandledSeq != null && provenance.seq <= lastHandledSeq) return null

  const groups = resolveEchoGroups(provenance.command)
  if (groups.length === 0) return null

  const selectors = new Set(acc?.selectors ?? [])
  groups.forEach((sel) => selectors.add(sel))

  return {
    selectors,
    firstAt: acc?.firstAt ?? provenance.at,
    lastAt: provenance.at,
    lastSeq: provenance.seq,
  }
}

export function IonActionEcho() {
  const [location] = useLocation()
  const [chip, setChip] = useState(null) // { top, left } | null

  const visible = isIonVisible({
    pathname: location,
    access: chatAccess.value,
    ionHidden: ionHidden.value,
  })
  // Read inside an effect/callback below, where the render-scope `visible`
  // would otherwise be stale by the time a coalesce window flushes.
  const visibleRef = useRef(visible)
  visibleRef.current = visible

  const lastHandledSeqRef = useRef(null)
  const pendingRef = useRef(null)
  const windowTimerRef = useRef(null)
  // I1 fix: removal timers keyed PER TARGET ELEMENT instead of one shared
  // timer ref. A shared timer meant flush() for a SECOND echo (e.g.
  // intro-drive's filter-echo then view-echo, back to back) discarded the
  // FIRST echo's removal timeout via clearTimeout — without ever removing
  // its classes — so the first echo's targets kept `.ion-action-echo-pulse`
  // forever. Each target now owns its own timer, so a later flush touching
  // DIFFERENT elements can never cancel an earlier element's pending removal.
  const pulseTimersRef = useRef(new Map())
  const chipTimerRef = useRef(null)

  function flush() {
    windowTimerRef.current = null
    const acc = pendingRef.current
    pendingRef.current = null
    if (!acc || !visibleRef.current) return

    const targets = []
    acc.selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => targets.push(el))
    })
    if (targets.length === 0) return

    targets.forEach((el) => {
      el.classList.add('ion-action-echo-pulse')
      const existingTimer = pulseTimersRef.current.get(el)
      if (existingTimer) clearTimeout(existingTimer)
      const timerId = setTimeout(() => {
        el.classList.remove('ion-action-echo-pulse')
        pulseTimersRef.current.delete(el)
      }, PULSE_DURATION_MS)
      pulseTimersRef.current.set(el, timerId)
    })

    const rect = targets[0].getBoundingClientRect()
    setChip({ top: rect.bottom + 8, left: rect.left })
    if (chipTimerRef.current) clearTimeout(chipTimerRef.current)
    chipTimerRef.current = setTimeout(() => {
      setChip(null)
      chipTimerRef.current = null
    }, CHIP_DURATION_MS)
  }

  useSignalEffect(() => {
    const provenance = commandProvenance.value
    const next = accumulateEcho(pendingRef.current, provenance, lastHandledSeqRef.current)
    if (!next) return

    pendingRef.current = next
    lastHandledSeqRef.current = next.lastSeq
    // A window is already running — its eventual flush will pick up this
    // write too (it reads pendingRef.current, not a closed-over snapshot).
    if (windowTimerRef.current) return
    windowTimerRef.current = setTimeout(flush, COALESCE_WINDOW_MS)
  })

  useEffect(() => {
    return () => {
      if (windowTimerRef.current) clearTimeout(windowTimerRef.current)
      pulseTimersRef.current.forEach((timerId) => clearTimeout(timerId))
      pulseTimersRef.current.clear()
      if (chipTimerRef.current) clearTimeout(chipTimerRef.current)
    }
  }, [])

  if (!visible) return null
  if (typeof document === 'undefined') return null
  if (!chip) return null

  return createPortal(
    <div
      class="ion-action-echo-chip"
      style={{ top: `${chip.top}px`, left: `${chip.left}px` }}
      aria-hidden="true"
    >
      ✦ by Ion
    </div>,
    document.body
  )
}
